import { Suspense, lazy, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import CurrencySelector from '@/components/CurrencySelector';
import Plans from '@/components/Plans';
import Spinner from '@/components/Spinner';
import { SITE_NAME } from '@/config';
import { useShallowEqualSelector } from '@/hooks';
import { usePlans } from '@/hooks/price';
import {
	useIsCollaborator,
	useIsExtendedCollaborator,
	useShowCurrencySelector
} from '@/hooks/user';
import { SUBSCRIPTION_STATUS } from '@/models/subscription';
import { setPlansModalOpen } from '@/store/checkout';
import { clearBillingData } from '@/store/profile';
import { fetchBillingInfo } from '@/store/profile/thunks';
import { showError } from '@/utils';

import Subscription from './Subscription/Subscription';

const FaqSection = lazy(() => import('./Faqs'));

function Billing() {
	const { t } = useTranslation();
	const location = useLocation();
	const dispatch = useDispatch();
	const allowCurrencySelector = useShowCurrencySelector();
	const isCollaborator = useIsCollaborator();
	const isExtendedCollaborator = useIsExtendedCollaborator();
	const plansData = usePlans();

	const { plans = [] } = plansData || {};

	const { subscriptionInfo, hasPayments } = useShallowEqualSelector(state => ({
		subscriptionInfo: state.profile?.subscriptionInfo,
		hasPayments: !state.billing?.isEmpty
	}));

	const { completed: ready, loading } = useSelector(
		state => state.profile.fetchBillingInfo
	);

	const isPending = subscriptionInfo?.status === SUBSCRIPTION_STATUS.PENDING;

	const shouldRenderPlans =
		!loading &&
		ready &&
		(!subscriptionInfo || (isPending && !hasPayments)) &&
		!isCollaborator &&
		!isExtendedCollaborator;

	useEffect(() => {
		(async () => {
			try {
				await dispatch(fetchBillingInfo()).unwrap();
			} catch (error) {
				showError(error);
			}
		})();

		return () => {
			dispatch(clearBillingData());
		};
	}, []);

	useEffect(() => {
		if (location?.state?.openChangePlanModal) {
			dispatch(setPlansModalOpen(true));
		}
	}, [location]);

	return (
		<div className="flex w-full flex-col justify-center gap-12 pb-5 lg:gap-16">
			{loading && !ready && (
				<Spinner
					className="absolute inset-0 m-auto text-grey-500"
					size={24}
				/>
			)}

			{!loading &&
				ready &&
				subscriptionInfo?.status &&
				!shouldRenderPlans && (
					<div className="mt-4 flex w-full flex-col gap-12 md:mt-[32px] lg:gap-16">
						<Subscription />
					</div>
				)}

			{shouldRenderPlans && (
				<div className="flex w-full flex-col gap-5">
					<div className="mt-4 flex w-full items-start justify-between gap-3 md:mt-[32px] lg:items-end">
						<div className="flex w-full flex-col items-start justify-between gap-3 md:flex-row md:items-end md:gap-2">
							<h2 className="text-xl font-semibold">
								{t('billing.unsubscribedTitle', {
									siteName: SITE_NAME
								})}
							</h2>
						</div>
						<CurrencySelector
							className="w-auto"
							disabled={!allowCurrencySelector}
							prices={plans}
						/>
					</div>

					<Plans
						plansData={plansData}
						featuresProps={{ className: 'mt-0 pb-0' }}
					/>
				</div>
			)}

			{shouldRenderPlans && (
				<Suspense>
					<FaqSection plansData={plansData} />
				</Suspense>
			)}
		</div>
	);
}

export default Billing;
